import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import "tom-select/dist/css/tom-select.css";

export default class extends Controller {
  static targets = ["select"];
  static values = {
    initialOptions: Array,
    selected: Array,
  };

  connect() {
    this.initializeTomSelect();
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy();
    }
  }

  initializeTomSelect() {
    // Merge selected items into options to ensure they're available
    const allOptions = [...this.initialOptionsValue];

    // Add selected items that aren't already in initial options
    this.selectedValue.forEach((selectedItem) => {
      const exists = allOptions.some((option) => option.id === selectedItem.id);
      if (!exists) {
        allOptions.push(selectedItem);
      }
    });

    this.tomSelect = new TomSelect(this.selectTarget, {
      valueField: "id",
      labelField: "name",
      searchField: "name",
      highlight: false,
      plugins: {
        remove_button: {
          title: "Remove Location",
          className:
            "remove-selected-item px-1 text-gray-300 hover:text-gray-400",
        },
      },
      options: allOptions,
      items: this.selectedValue.map((item) => item.id),
      optgroups: [
        { value: "City", label: "Cities" },
        { value: "County", label: "Counties" },
        { value: "Subdivision", label: "Neighborhoods" },
        { value: "PostalCode", label: "Zip Codes" },
        { value: "Address", label: "Addresses" },
      ],
      persist: false,
      createOnBlur: false,
      create: false,
      maxItems: null,
      hideSelected: false,
      closeAfterSelect: true,
      allowEmptyOption: true,
      loadThrottle: 300,
      maxOptions: 50,
      preload: true,

      load: this.handleLoad.bind(this),
      render: {
        no_results: () => this.renderNoResults(),
        optgroup_header: (data, escape) =>
          this.renderOptgroupHeader(data, escape),
        option: (data, escape) => this.renderOption(data, escape),
        item: (data, escape) => this.renderItem(data, escape),
        loading: () => this.renderLoading(),
        undefined: () => this.renderUndefined(),
      },
    });
  }

  async handleLoad(query, callback) {
    if (!query.length) {
      callback(this.initialOptionsValue);
      return;
    }

    const url = this.selectTarget.dataset.url;
    if (!url) {
      callback();
      return;
    }

    try {
      const response = await fetch(`${url}?query=${encodeURIComponent(query)}`);
      if (!response.ok) throw new Error("Network response was not ok");

      const json = await response.json();

      // Assign optgroups (backend already provides correct order)
      json.forEach((item) => {
        if (item.type) {
          item.optgroup = item.type;
        }
      });

      callback(json);
    } catch (error) {
      console.error("Loading error:", error);
      callback();
    }
  }

  renderNoResults() {
    return `
      <div class="p-2 text-center text-sm text-gray-500">
        No locations found
      </div>
    `;
  }

  renderOptgroupHeader(data, escape) {
    return `
      <div class="px-3 py-2 text-xs font-semibold tracking-wider text-gray-600 bg-gray-100 border-y border-gray-200 uppercase">
        ${escape(data.label)}
      </div>
    `;
  }

  renderOption(data, escape) {
    if (data.type === "Address") {
      return this.renderAddressOption(data, escape);
    }
    return this.renderLocationOption(data, escape);
  }

  renderAddressOption(data, escape) {
    return `
      <div class="relative cursor-default select-none py-2 pl-3 pr-12 hover:bg-gray-200 hover:text-gray-900">
        <div>
          <span class="font-medium">${escape(data.name)}</span>
          <div class="text-sm text-gray-500">
            ${data.city ? `${escape(data.city)}${data.state ? `, ${escape(data.state)}` : ""}` : ""}
          </div>
        </div>
      </div>
    `;
  }

  renderLocationOption(data, escape) {
    const displayType =
      data.type === "Subdivision" ? "Neighborhood" : data.type;
    return `
      <div class="relative cursor-default select-none py-2 pl-3 pr-20 hover:bg-gray-200 hover:text-gray-900">
        <span class="font-medium">${escape(data.name)}${data.state ? `, ${escape(data.state)}` : ""}</span>
        <span class="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">${escape(displayType || "")}</span>
      </div>
    `;
  }

  renderItem(data, escape) {
    return `
      <div class="flex items-center pr-8">
        <span class="font-medium">${escape(data.name)}${data.state ? `, ${escape(data.state)}` : ""}</span>
      </div>
    `;
  }

  renderLoading() {
    return `
      <div class="p-2 text-center text-sm text-gray-500">
        Loading...
      </div>
    `;
  }

  renderUndefined() {
    return `
      <div class="p-2 text-center text-sm text-gray-500">
        Type to search locations or addresses
      </div>
    `;
  }
}
